import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EastIcon from "@mui/icons-material/East";
import NorthIcon from "@mui/icons-material/North";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import SouthIcon from "@mui/icons-material/South";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import TuneIcon from "@mui/icons-material/Tune";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import WestIcon from "@mui/icons-material/West";
import classNames from "classnames";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChangeEvent,
  KeyboardEvent,
  SyntheticEvent,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectToken } from "../../../app/slices/AuthenticationSlice";
import {
  getPropRenderThumbnailCacheKey,
  selectCachedPropRenderThumbnails,
  setCachedPropRenderThumbnail,
} from "../../../app/slices/PropLibrarySlice";
import {
  selectAllPropPresets,
  selectSelectedElement,
  selectSelectedTake3D,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import { PiInteractionDropdown } from "../../../components/PiInteractionDropdown";
import type { ClickableOverlay } from "../../../dto/PropMetadata";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import usePropHelper from "../../../hooks/PropHelper";
import { PropType } from "../../../models/Prop";
import type {
  IPropLabelQuiz,
  IPropLabelQuizAnswer,
  PropLabelQuizPreferredSide,
} from "../../../models/interactions/PropLabelQuiz";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import PiQuizCheckbox from "../components/PiQuizCheckbox";
import PiQuizTextfield from "../components/PiQuizTextfield";
import PropLabelQuizInspectPicker, {
  clampNormalizedCoordinate,
  getContainedImageBounds,
  getPropLabelQuizAttachedLabelPlacement,
  getPropLabelQuizInspectPreviewItems,
  type ContainedImageBounds,
  type PropLabelQuizAttachedLabelDot,
  type PropLabelQuizInspectPreviewItem,
  type PropLabelQuizInspectPropOption,
} from "./PropLabelQuizInspectPicker";
import { PropService } from "../../../services/PropService";
import "./PropLabelQuizPreviewBody.css";

const maxAnswers = 3;
const minDistanceBias = 0.5;
const maxDistanceBias = 3;
const distanceBiasStep = 0.1;
const defaultDistanceBias = 1;
const autoPreferredSide: PropLabelQuizPreferredSide = "Auto";
const overviewPreviewZoom = 1.18;
const emptyBounds: ContainedImageBounds = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
};

const getSelectedOverviewPreview = (
  answer: IPropLabelQuizAnswer,
  previewItems: PropLabelQuizInspectPreviewItem[],
) => {
  if (previewItems.length === 0) return undefined;

  if (answer.inspectDefinitionId) {
    const inspectPreview = previewItems.find(
      (item) => item.id === answer.inspectDefinitionId,
    );

    if (inspectPreview) return inspectPreview;
  }

  return previewItems.find((item) =>
    item.overlays.some((overlay) => overlay.clickableName === answer.tag),
  );
};

const getSelectedOverviewOverlay = (
  answer: IPropLabelQuizAnswer,
  preview: PropLabelQuizInspectPreviewItem | undefined,
) =>
  preview?.overlays.find((overlay) => overlay.clickableName === answer.tag);

const getClampedFocusedOffset = (
  frameSize: number,
  contentSize: number,
  focusOffset: number,
  targetOffset: number,
) => {
  if (contentSize <= frameSize) return (frameSize - contentSize) / 2;

  return Math.min(0, Math.max(frameSize - contentSize, targetOffset - focusOffset));
};

const getFocusedZoomImageBounds = (
  frameWidth: number,
  frameHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  focus: ClickableOverlay["position"],
): ContainedImageBounds => {
  if (
    frameWidth <= 0 ||
    frameHeight <= 0 ||
    naturalWidth <= 0 ||
    naturalHeight <= 0
  ) {
    return {
      left: 0,
      top: 0,
      width: frameWidth,
      height: frameHeight,
    };
  }

  const containedBounds = getContainedImageBounds(
    frameWidth,
    frameHeight,
    naturalWidth,
    naturalHeight,
  );
  const width = containedBounds.width * overviewPreviewZoom;
  const height = containedBounds.height * overviewPreviewZoom;
  const focusX = clampNormalizedCoordinate(focus.x) * width;
  const focusY = clampNormalizedCoordinate(focus.y) * height;

  return {
    left: getClampedFocusedOffset(frameWidth, width, focusX, frameWidth / 2),
    top: getClampedFocusedOffset(frameHeight, height, focusY, frameHeight / 2),
    width,
    height,
  };
};

function PropLabelQuizAnswerOverviewPreview(props: {
  answer: IPropLabelQuizAnswer;
  selectedProp: PropLabelQuizInspectPropOption | undefined;
}) {
  const { answer, selectedProp } = props;
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const token = useSelector(selectToken);
  const cachedPropRenderThumbnails = useSelector(
    selectCachedPropRenderThumbnails,
  );
  const frameRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const inFlightThumbnailKeyRef = useRef<string | undefined>();
  const [frameBounds, setFrameBounds] =
    useState<ContainedImageBounds>(emptyBounds);
  const [imageBounds, setImageBounds] =
    useState<ContainedImageBounds>(emptyBounds);

  const previewItems = useMemo(
    () => getPropLabelQuizInspectPreviewItems(selectedProp),
    [selectedProp],
  );
  const selectedPreview = useMemo(
    () => getSelectedOverviewPreview(answer, previewItems),
    [answer, previewItems],
  );
  const selectedOverlay = getSelectedOverviewOverlay(answer, selectedPreview);
  const thumbnailKey =
    selectedProp && selectedPreview
      ? getPropRenderThumbnailCacheKey(
          0,
          selectedProp.propId,
          selectedPreview.id,
        )
      : undefined;
  const thumbnailUri = thumbnailKey
    ? cachedPropRenderThumbnails[thumbnailKey]
    : undefined;

  const updateImageBounds = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const nextFrameBounds = {
      left: 0,
      top: 0,
      width: frame.clientWidth,
      height: frame.clientHeight,
    };

    setFrameBounds((previousBounds) =>
      previousBounds.width === nextFrameBounds.width &&
      previousBounds.height === nextFrameBounds.height
        ? previousBounds
        : nextFrameBounds,
    );

    const image = imageRef.current;
    if (!image) return;

    const nextImageBounds = selectedOverlay
      ? getFocusedZoomImageBounds(
          nextFrameBounds.width,
          nextFrameBounds.height,
          image.naturalWidth,
          image.naturalHeight,
          selectedOverlay.position,
        )
      : emptyBounds;

    setImageBounds((previousBounds) =>
      previousBounds.left === nextImageBounds.left &&
      previousBounds.top === nextImageBounds.top &&
      previousBounds.width === nextImageBounds.width &&
      previousBounds.height === nextImageBounds.height
        ? previousBounds
        : nextImageBounds,
    );
  }, [selectedOverlay]);

  useEffect(() => {
    if (
      !currentOrganization?.id ||
      !token ||
      !selectedProp ||
      selectedProp.propType !== PropType.Default ||
      !selectedPreview ||
      !thumbnailKey ||
      thumbnailUri ||
      inFlightThumbnailKeyRef.current === thumbnailKey
    ) {
      return;
    }

    let cancelled = false;
    inFlightThumbnailKeyRef.current = thumbnailKey;

    PropService.GetPropRenderThumbnail(
      currentOrganization.id,
      token,
      0,
      selectedProp.propId,
      selectedPreview.id,
    ).then((thumbnail) => {
      if (inFlightThumbnailKeyRef.current === thumbnailKey) {
        inFlightThumbnailKeyRef.current = undefined;
      }

      if (cancelled || !thumbnail?.downloadUri) return;

      dispatch(
        setCachedPropRenderThumbnail({
          propType: 0,
          propId: selectedProp.propId,
          renderId: selectedPreview.id,
          downloadUri: thumbnail.downloadUri,
        }),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [
    currentOrganization?.id,
    dispatch,
    selectedPreview,
    selectedProp,
    thumbnailKey,
    thumbnailUri,
    token,
  ]);

  useEffect(() => {
    setFrameBounds(emptyBounds);
    setImageBounds(emptyBounds);
    updateImageBounds();

    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateImageBounds);
    observer.observe(frame);

    return () => observer.disconnect();
  }, [selectedPreview?.id, thumbnailUri, updateImageBounds]);

  const canRenderPreview = Boolean(
    selectedPreview && selectedOverlay && thumbnailUri,
  );
  const selectedDot = useMemo<PropLabelQuizAttachedLabelDot | undefined>(() => {
    if (
      !canRenderPreview ||
      !selectedOverlay ||
      imageBounds.width <= 0 ||
      imageBounds.height <= 0
    ) {
      return undefined;
    }

    return {
      id: selectedOverlay.clickableName,
      label: selectedOverlay.clickableName,
      left:
        imageBounds.left +
        clampNormalizedCoordinate(selectedOverlay.position.x) *
          imageBounds.width,
      top:
        imageBounds.top +
        clampNormalizedCoordinate(selectedOverlay.position.y) *
          imageBounds.height,
    };
  }, [canRenderPreview, imageBounds, selectedOverlay]);
  const selectedLabelPlacement = selectedDot
    ? getPropLabelQuizAttachedLabelPlacement(
        selectedDot,
        [selectedDot],
        frameBounds,
      )
    : undefined;
  const fallbackLabel = (() => {
    if (!selectedProp) return "Select a Prop first";
    if (!answer.tag) return "Select a Clickable tag";
    if (!selectedPreview || !selectedOverlay) {
      return "Selected Clickable tag has no inspect preview";
    }
    return "Inspect preview unavailable";
  })();

  return (
    <div className="proplabelquiz-overview-preview" ref={frameRef}>
      {canRenderPreview && selectedPreview && thumbnailUri ? (
        <>
          <img
            ref={imageRef}
            className="proplabelquiz-overview-preview-image"
            src={thumbnailUri}
            alt={selectedPreview.displayName}
            draggable={false}
            style={{
              left: `${imageBounds.left}px`,
              top: `${imageBounds.top}px`,
              width: `${imageBounds.width}px`,
              height: `${imageBounds.height}px`,
            }}
            onLoad={updateImageBounds}
            onDragStart={(event) => event.preventDefault()}
          />
          {selectedDot && (
            <div
              className={classNames(
                "proplabelquiz-overview-preview-dot proplabelquiz-inspectpicker-dot-selected",
                {
                  "proplabelquiz-inspectpicker-dot-correct": answer.correct,
                },
              )}
              style={{
                left: `${selectedDot.left}px`,
                top: `${selectedDot.top}px`,
              }}
            />
          )}
          {selectedDot && selectedLabelPlacement && (
            <>
              <div
                className={classNames(
                  "proplabelquiz-overview-preview-label-connector",
                  {
                    "proplabelquiz-overview-preview-label-correct":
                      answer.correct,
                  },
                )}
                style={{
                  left: `${selectedLabelPlacement.connectorLeft}px`,
                  top: `${selectedLabelPlacement.connectorTop}px`,
                  width: `${selectedLabelPlacement.connectorWidth}px`,
                  transform: `translateY(-50%) rotate(${
                    selectedLabelPlacement.connectorRotation
                  }deg)`,
                }}
              />
              <div
                className={classNames("proplabelquiz-overview-preview-label", {
                  "proplabelquiz-overview-preview-label-correct":
                    answer.correct,
                })}
                data-testid="prop-label-quiz-overview-selected-dot-label"
                style={{
                  left: `${selectedLabelPlacement.left}px`,
                  top: `${selectedLabelPlacement.top}px`,
                  width: `${selectedLabelPlacement.width}px`,
                  height: `${selectedLabelPlacement.height}px`,
                }}
              >
                <span>{selectedDot.label}</span>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="proplabelquiz-overview-preview-empty">
          <PhotoLibraryOutlinedIcon fontSize="inherit" color="inherit" />
          <span>{fallbackLabel}</span>
        </div>
      )}
    </div>
  );
}
const preferredSideOptions: {
  value: PropLabelQuizPreferredSide;
  label: string;
  className: string;
  Icon: typeof TuneIcon;
}[] = [
  {
    value: autoPreferredSide,
    label: "Auto",
    className: "auto",
    Icon: TuneIcon,
  },
  {
    value: "TopLeft",
    label: "Top Left",
    className: "top-left",
    Icon: NorthWestIcon,
  },
  {
    value: "Top",
    label: "Top",
    className: "top",
    Icon: NorthIcon,
  },
  {
    value: "TopRight",
    label: "Top Right",
    className: "top-right",
    Icon: NorthEastIcon,
  },
  {
    value: "Left",
    label: "Left",
    className: "left",
    Icon: WestIcon,
  },
  {
    value: "Right",
    label: "Right",
    className: "right",
    Icon: EastIcon,
  },
  {
    value: "BottomLeft",
    label: "Bottom Left",
    className: "bottom-left",
    Icon: SouthWestIcon,
  },
  {
    value: "Bottom",
    label: "Bottom",
    className: "bottom",
    Icon: SouthIcon,
  },
  {
    value: "BottomRight",
    label: "Bottom Right",
    className: "bottom-right",
    Icon: SouthEastIcon,
  },
];

type PropLabelQuizPreviewBodyProps = {
  detailBackRequest?: number;
  ordered?: boolean;
  onActiveDetailAnswerChange?: (answerId: string | null) => void;
};

type PropLabelOrderDragHandleProps = {
  attributes: HTMLAttributes<HTMLDivElement>;
  listeners?: HTMLAttributes<HTMLDivElement>;
};

function PropLabelOrderSortableCard(props: {
  answer: IPropLabelQuizAnswer;
  index: number;
  renderAnswerCard: (
    answer: IPropLabelQuizAnswer,
    index: number,
    dragHandleProps?: PropLabelOrderDragHandleProps,
  ) => ReactNode;
}) {
  const { answer, index, renderAnswerCard } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: answer.id,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.72 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames("proplabelquiz-draggable", {
        "proplabelquiz-draggable-active": isDragging,
      })}
    >
      {renderAnswerCard(answer, index, {
        attributes: attributes as HTMLAttributes<HTMLDivElement>,
        listeners: listeners as HTMLAttributes<HTMLDivElement>,
      })}
    </div>
  );
}

export default function PropLabelQuizPreviewBody(
  props: PropLabelQuizPreviewBodyProps = {},
) {
  const { detailBackRequest, ordered = false, onActiveDetailAnswerChange } = props;
  const quiz = useSelector(selectSelectedElement) as IPropLabelQuiz;
  const take = useSelector(selectSelectedTake3D);
  const allPropPresets = useSelector(selectAllPropPresets);
  const dispatch = useDispatch();
  const { getValue, changeValue, getNewKey } = useTrainingLocalization();
  const { getUsedPropByInstanceId } = usePropHelper();
  const dragSensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
  );
  const canDelete = quiz.answers.length > 1;
  const canAdd = quiz.answers.length < maxAnswers;
  const [activeAnswerId, setActiveAnswerId] = useState<string | null>(
    quiz.answers[0]?.id ?? null,
  );
  const [view, setView] = useState<"overview" | "detail">("overview");
  const lastDetailBackRequest = useRef(detailBackRequest);

  const propOptions = useMemo<PropLabelQuizInspectPropOption[]>(() => {
    const propIds = new Set<string>();

    take?.props.forEach((prop) => propIds.add(prop.instanceId));
    Object.keys(take?.attachments ?? {}).forEach((propId) => propIds.add(propId));

    return Array.from(propIds).map((propId) => {
      const preset = allPropPresets.find((prop) => prop.id === propId);
      let clickableTags: string[] = [];
      let inspectDefinitions: PropLabelQuizInspectPropOption["inspectDefinitions"] =
        [];
      let clickableOverlaysByInspectId:
        | PropLabelQuizInspectPropOption["clickableOverlaysByInspectId"]
        | undefined;

      try {
        const usedProp = getUsedPropByInstanceId(propId);
        clickableTags =
          usedProp.metadata.clickableTags?.map((tag) => tag.name) ?? [];
        inspectDefinitions = usedProp.metadata.inspectDefinitions ?? [];
        clickableOverlaysByInspectId =
          usedProp.metadata.clickableOverlaysByInspectId;
      } catch {
        clickableTags = [];
        inspectDefinitions = [];
        clickableOverlaysByInspectId = undefined;
      }

      return {
        id: propId,
        propId: preset?.propId ?? "",
        propType: preset?.propType ?? PropType.Environment,
        name: preset?.name ?? "Unknown prop",
        clickableTags,
        inspectDefinitions,
        clickableOverlaysByInspectId,
      };
    });
  }, [
    allPropPresets,
    getUsedPropByInstanceId,
    take?.attachments,
    take?.props,
  ]);

  const activeAnswer = useMemo(
    () =>
      quiz.answers.find((answer) => answer.id === activeAnswerId) ??
      quiz.answers[0],
    [activeAnswerId, quiz.answers],
  );

  useEffect(() => {
    onActiveDetailAnswerChange?.(
      view === "detail" && activeAnswer ? activeAnswer.id : null,
    );
  }, [activeAnswer, onActiveDetailAnswerChange, view]);

  useEffect(() => {
    if (detailBackRequest === lastDetailBackRequest.current) return;

    lastDetailBackRequest.current = detailBackRequest;
    setView("overview");
  }, [detailBackRequest]);

  const updateQuiz = (updatedQuiz: IPropLabelQuiz) => {
    dispatch(updateSelectedInteraction(updatedQuiz));
  };

  const updateAnswer = (
    answerId: string,
    patch: Partial<IPropLabelQuizAnswer>,
  ) => {
    updateQuiz({
      ...quiz,
      answers: quiz.answers.map((answer) =>
        answer.id === answerId ? { ...answer, ...patch } : answer,
      ),
    });
  };

  const reorderAnswers = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const startIndex = quiz.answers.findIndex(
      (answer) => answer.id === active.id,
    );
    const destinationIndex = quiz.answers.findIndex(
      (answer) => answer.id === over.id,
    );

    if (startIndex < 0 || destinationIndex < 0) return;

    const answers = [...quiz.answers];
    const [movedAnswer] = answers.splice(startIndex, 1);
    answers.splice(destinationIndex, 0, movedAnswer);

    updateQuiz({
      ...quiz,
      answers,
    });
  };

  const addAnswer = () => {
    if (!canAdd) return;
    const newAnswerId = GetNewGuid();

    updateQuiz({
      ...quiz,
      answers: [
        ...quiz.answers,
        {
          id: newAnswerId,
          correct: false,
          text: getNewKey(),
          propGuid: "",
          tag: "",
          preferredSide: autoPreferredSide,
          distanceBias: defaultDistanceBias,
        },
      ],
    });
    setActiveAnswerId(newAnswerId);
    setView("detail");
  };

  const deleteAnswer = (answerId: string) => {
    if (!canDelete) return;
    const remainingAnswers = quiz.answers.filter(
      (answer) => answer.id !== answerId,
    );

    updateQuiz({
      ...quiz,
      answers: remainingAnswers,
    });
    setActiveAnswerId(remainingAnswers[0]?.id ?? null);
    setView("overview");
  };

  const openDetail = (answerId: string) => {
    setActiveAnswerId(answerId);
    setView("detail");
  };

  const openDetailWithKeyboard = (
    event: KeyboardEvent<HTMLDivElement>,
    answerId: string,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openDetail(answerId);
    }
  };

  const stopCardNavigation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const getPropName = (propGuid: string) =>
    propOptions.find((prop) => prop.id === propGuid)?.name ?? "No Prop";

  const getSelectedPropOption = (propGuid: string) =>
    propOptions.find((prop) => prop.id === propGuid);

  const getDistanceBiasValue = (distanceBias: number | undefined) =>
    typeof distanceBias === "number" && Number.isFinite(distanceBias)
      ? distanceBias
      : defaultDistanceBias;

  const formatDistanceBias = (distanceBias: number | undefined) =>
    `${getDistanceBiasValue(distanceBias).toFixed(1).replace(".0", "")}x`;

  const clampDistanceBias = (distanceBias: number) =>
    Math.min(Math.max(distanceBias, minDistanceBias), maxDistanceBias);

  const getDistanceBiasProgress = (distanceBias: number | undefined) =>
    ((getDistanceBiasValue(distanceBias) - minDistanceBias) /
      (maxDistanceBias - minDistanceBias)) *
    100;

  const updateDistanceBias = (
    answer: IPropLabelQuizAnswer,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const distanceBias = Number(event.target.value);

    updateAnswer(answer.id, {
      distanceBias: Number.isFinite(distanceBias)
        ? clampDistanceBias(distanceBias)
        : defaultDistanceBias,
    });
  };

  const updatePropTarget = (answer: IPropLabelQuizAnswer, propGuid: string) => {
    updateAnswer(answer.id, {
      propGuid,
      tag: "",
      inspectDefinitionId: undefined,
    });
  };

  const renderPropDropdown = (
    answer: IPropLabelQuizAnswer,
    label = "Prop",
  ) => {
    const propDropdownItems = propOptions.map((prop) => (
      <PiInteractionDropdown.Item
        key={prop.id}
        onClick={() => updatePropTarget(answer, prop.id)}
      >
        <div className="proplabelquiz-dropdown-item">
          <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
          <div>{prop.name}</div>
        </div>
      </PiInteractionDropdown.Item>
    ));

    return (
      <div className="proplabelquiz-interaction-dropdown">
        <PiInteractionDropdown label={label}>
          <PiInteractionDropdown.Trigger>
            <div className="proplabelquiz-dropdown-trigger">
              <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
              <div>{getPropName(answer.propGuid)}</div>
            </div>
          </PiInteractionDropdown.Trigger>
          {propDropdownItems}
        </PiInteractionDropdown>
      </div>
    );
  };

  const renderPreferredSide = (answer: IPropLabelQuizAnswer) => (
    <div className="proplabelquiz-side-toggle">
      {preferredSideOptions.map(({ value, label, className, Icon }) => {
        const selected = answer.preferredSide === value;

        return (
          <button
            key={value}
            type="button"
            aria-label={`${label} placement`}
            title={`${label} placement`}
            className={classNames(
              "proplabelquiz-side-button",
              `proplabelquiz-side-button-${className}`,
              {
                "proplabelquiz-side-button-selected": selected,
              },
            )}
            onClick={() => updateAnswer(answer.id, { preferredSide: value })}
          >
            <Icon fontSize="inherit" color="inherit" />
            {value === autoPreferredSide && (
              <span className="proplabelquiz-side-label">Auto</span>
            )}
          </button>
        );
      })}
    </div>
  );

  const renderCorrectField = (
    answer: IPropLabelQuizAnswer,
    className?: string,
  ) => (
    <div className={classNames("proplabelquiz-correct-field", className)}>
      <PiQuizCheckbox
        checked={answer.correct}
        onChange={(correct) => updateAnswer(answer.id, { correct })}
      />
    </div>
  );

  const renderOrderField = (
    index: number,
    dragHandleProps?: PropLabelOrderDragHandleProps,
  ) => (
    <div
      {...dragHandleProps?.attributes}
      {...dragHandleProps?.listeners}
      className="proplabelquiz-order-field"
      aria-label={`Move Quiz answer ${index + 1}`}
      title="Drag to reorder"
      onClick={stopCardNavigation}
      onKeyDown={stopCardNavigation}
    >
      <span>{index + 1}</span>
    </div>
  );

  const renderAnswerTextField = (answer: IPropLabelQuizAnswer) => (
    <PiQuizTextfield
      value={getValue(answer.text)}
      onChange={(value) => changeValue(answer.text, value)}
    />
  );

  const renderAnswerCard = (
    answer: IPropLabelQuizAnswer,
    index: number,
    dragHandleProps?: PropLabelOrderDragHandleProps,
  ) => {
    const hasProp = Boolean(answer.propGuid);
    const hasTag = Boolean(answer.tag);
    const selectedProp = getSelectedPropOption(answer.propGuid);

    return (
      <div
        className={classNames("proplabelquiz-card", {
          "proplabelquiz-card-incomplete": !hasProp || !hasTag,
        })}
        key={answer.id}
        role="button"
        tabIndex={0}
        aria-label={`Details for Quiz answer ${index + 1}`}
        onClick={() => openDetail(answer.id)}
        onKeyDown={(event) => openDetailWithKeyboard(event, answer.id)}
      >
        {canDelete && (
          <button
            type="button"
            className="proplabelquiz-card-remove"
            aria-label={`Remove Quiz answer ${index + 1}`}
            title="Remove Quiz answer"
            onClick={(event) => {
              event.stopPropagation();
              deleteAnswer(answer.id);
            }}
          >
            <ClearIcon fontSize="inherit" color="inherit" />
          </button>
        )}
        <div className="proplabelquiz-card-target">
          <PropLabelQuizAnswerOverviewPreview
            answer={answer}
            selectedProp={selectedProp}
          />
        </div>
        <div className="proplabelquiz-card-footer">
          <div
            className="proplabelquiz-card-correct"
            onClick={stopCardNavigation}
            onKeyDown={stopCardNavigation}
          >
            {ordered
              ? renderOrderField(index, dragHandleProps)
              : renderCorrectField(answer)}
          </div>
          <div
            className="proplabelquiz-card-text"
            onClick={stopCardNavigation}
            onKeyDown={stopCardNavigation}
          >
            {renderAnswerTextField(answer)}
          </div>
        </div>
      </div>
    );
  };

  const renderOverview = () => (
    <div className="proplabelquiz-overview">
      {ordered ? (
        <DndContext
          sensors={dragSensors}
          collisionDetection={closestCenter}
          onDragEnd={reorderAnswers}
        >
          <SortableContext
            items={quiz.answers.map((answer) => answer.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div className="proplabelquiz-overview-track pit-scrollbar">
              {quiz.answers.map((answer, index) => (
                <PropLabelOrderSortableCard
                  key={answer.id}
                  answer={answer}
                  index={index}
                  renderAnswerCard={renderAnswerCard}
                />
              ))}
              {canAdd && (
                <button
                  type="button"
                  className="proplabelquiz-add-card"
                  onClick={addAnswer}
                >
                  <div className="proplabelquiz-add-card-body">
                    <AddIcon fontSize="inherit" color="inherit" />
                    <span>Add Quiz answer</span>
                  </div>
                </button>
              )}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="proplabelquiz-overview-track pit-scrollbar">
          {quiz.answers.map((answer, index) => renderAnswerCard(answer, index))}
          {canAdd && (
            <button
              type="button"
              className="proplabelquiz-add-card"
              onClick={addAnswer}
            >
              <div className="proplabelquiz-add-card-body">
                <AddIcon fontSize="inherit" color="inherit" />
                <span>Add Quiz answer</span>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderDetail = (answer: IPropLabelQuizAnswer) => {
    const selectedProp = getSelectedPropOption(answer.propGuid);

    return (
      <div className="proplabelquiz-detail">
        <div className="proplabelquiz-detail-fields pit-scrollbar">
          <div className="proplabelquiz-detail-layout">
            <aside
              className="proplabelquiz-detail-controls"
              aria-label="Quiz answer parameters"
            >
              <div className="proplabelquiz-detail-control-stack">
                <div className="proplabelquiz-detail-control-panel proplabelquiz-detail-prop-control">
                  {renderPropDropdown(answer)}
                </div>
              </div>
              <div className="proplabelquiz-detail-parameter-stack">
                <div className="proplabelquiz-detail-control-panel proplabelquiz-field proplabelquiz-field-distance">
                  <div className="proplabelquiz-distance-header">
                    <span>Distance bias</span>
                    <output>{formatDistanceBias(answer.distanceBias)}</output>
                  </div>
                  <input
                    type="range"
                    min={minDistanceBias}
                    max={maxDistanceBias}
                    step={distanceBiasStep}
                    value={getDistanceBiasValue(answer.distanceBias)}
                    aria-label="Distance bias"
                    className="proplabelquiz-distance-slider"
                    style={
                      {
                        "--proplabelquiz-slider-progress": `${getDistanceBiasProgress(
                          answer.distanceBias,
                        )}%`,
                      } as CSSProperties
                    }
                    onChange={(event) => updateDistanceBias(answer, event)}
                  />
                </div>
                <div className="proplabelquiz-field proplabelquiz-field-side">
                  <span>Placement</span>
                  {renderPreferredSide(answer)}
                </div>
              </div>
            </aside>
            <PropLabelQuizInspectPicker
              answer={answer}
              selectedProp={selectedProp}
              onSelectOverlay={(inspectDefinitionId, tag) =>
                updateAnswer(answer.id, {
                  propGuid: answer.propGuid,
                  tag,
                  inspectDefinitionId,
                })
              }
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="proplabelquiz-root">
      {view === "detail" && activeAnswer
        ? renderDetail(activeAnswer)
        : renderOverview()}
    </div>
  );
}
